import { faArrowLeft, faSearch, faTrash } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon, FontAwesomeIconProps } from "@fortawesome/react-fontawesome";
import debounce from "lodash-es/debounce";
import { stringify } from "query-string";
import React from "react";

import SearchResults from "@/mk2/apps/blogs/components/SearchResults";
import { counsellingsNewUrl, counsellingsTopicUrl } from "@/mk2/apps/counsellings/urls";
import { BrandDaysCampaignBanner } from "@/mk2/apps/forum/components/BrandDaysCampaignBanner";
import Feed from "@/mk2/apps/forum/components/Feed";
import TopicHeader, { TopicHeaderPlaceholder } from "@/mk2/apps/forum/components/TopicHeader";
import { READ_FEED_TIMEOUT, TESTINGS_TOPIC_ID } from "@/mk2/apps/forum/constants";
import { Props, TopicViewType } from "@/mk2/apps/forum/containers/Topic/TopicPage";
import { forumTopicsUrl } from "@/mk2/apps/forum/urls";
import AnnouncementBox, { BoxType } from "@/mk2/components/AnnouncementBox";
import { Btn, BtnType } from "@/mk2/components/Btn";
import { ClearableInput } from "@/mk2/components/ClearableInput";
import { Interpolate } from "@/mk2/components/Interpolate";
import { Link } from "@/mk2/components/Link";
import { url } from "@/mk2/helpers/urls";
import {
    BACK_TO_TOPICS,
    COUNSELLING_DO_SOMETHING_ABOUT_IT,
    COUNSELLING_SEARCH_COUNSELLING_PLACEHOLDER,
    COUNSELLING_SEARCH_COUNSELLING_RESULT_query_count,
    NO_QUESTIONS_FOR_THIS_COUNSELLING_action_btn,
    THERES_NO_POSTS_IN_TOPIC_DO_SOMETHING_WITH_IT,
    TOPIC_HAS_BEEN_DELETED,
} from "@/mk/autogenerated/translation";
import { isAuthenticatedAs } from "@/mk/bazaar/common/userUtils";

import styles from "./Topic.mscss";

const fasTrash: FontAwesomeIconProps = { icon: faTrash };

interface State {
    alreadyRead: boolean;
    query: string;
}

class Topic extends React.Component<Props, State> {
    private readTimer: number;
    private searchRedirectDebounced: () => void;

    constructor(props) {
        super(props);

        this.state = {
            alreadyRead: false,
            query: props.query,
        };

        this.searchRedirectDebounced = debounce(this.searchRedirect, 500);
    }

    public componentDidMount() {
        const { topic, counselling, onStoreEvent } = this.props;
        if (topic && counselling) {
            onStoreEvent("counselling_visit", {
                counselling_name: topic.slug,
            });
        }

        if (topic) {
            this.readTimer = window.setTimeout(this.onRead, READ_FEED_TIMEOUT);
        }
    }

    public componentDidUpdate(prevProps: Props) {
        const { topic, lastVisitedCount, counselling, onStoreEvent } = this.props;

        if (
            topic &&
            ((counselling &&
                prevProps.counselling &&
                counselling.id !== prevProps.counselling.id) ||
                (counselling && !prevProps.counselling))
        ) {
            onStoreEvent("counselling_visit", {
                counselling_name: topic.slug,
            });
        }

        if (topic && (lastVisitedCount || !this.state.alreadyRead)) {
            window.clearTimeout(this.readTimer);
            this.readTimer = window.setTimeout(this.onRead, READ_FEED_TIMEOUT);
        }
    }

    public componentWillUnmount() {
        window.clearTimeout(this.readTimer);
    }

    public shouldComponentUpdate(n: Props, nextState: State) {
        const p = this.props;

        return (
            p.posts !== n.posts ||
            p.after !== n.after ||
            p.hashtags.join(",") !== n.hashtags.join(",") ||
            p.isSubscribed !== n.isSubscribed ||
            p.complaintsStatistics !== n.complaintsStatistics ||
            p.query !== n.query ||
            p.loadingState !== n.loadingState ||
            this.state.query !== nextState.query
        );
    }

    public render() {
        const {
            topic,
            loadingState,
            isSubscribed,
            isFavorite,
            brandDaysCampaign,
            brandDaysCampaignGreetingText,
            counselling,
            counsellingPostsCount,
            requestUser,
            onSubscribe,
            onUnsubscribe,
            onAddToFavorites,
            onRemoveFromFavorites,
            permissions,
            staticContext,
            subscribers,
            isMobile,
            viewType,
            query,
            posts,
        } = this.props;

        const isModerator =
            topic &&
            isAuthenticatedAs(
                requestUser,
                topic.topicModerators?.map((tm) => tm.user) || topic.moderators,
            );
        const isAdmin = permissions.indexOf("forum.can_manage_forum") >= 0;
        const isDeleted = topic && !!topic.deletedAt;

        if (isDeleted && staticContext) {
            staticContext.statusCode = 410;
        }

        return (
            <div>
                {topic ? (
                    <div>
                        <TopicHeader
                            topic={topic}
                            counselling={counselling}
                            counsellingPostsCount={counsellingPostsCount}
                            topicLoadingState={loadingState}
                            isSubscribed={isSubscribed}
                            isFavorite={isFavorite}
                            requestUser={requestUser}
                            onSubscribe={onSubscribe}
                            onUnsubscribe={onUnsubscribe}
                            onAddToFavorites={onAddToFavorites}
                            onRemoveFromFavorites={onRemoveFromFavorites}
                            isModerator={isModerator}
                            subscribers={subscribers}
                            isMobile={isMobile}
                            viewType={viewType}
                        />
                        {brandDaysCampaign && (
                            <BrandDaysCampaignBanner
                                brandDaysCampaign={brandDaysCampaign}
                                brandDaysCampaignGreetingText={brandDaysCampaignGreetingText}
                            />
                        )}

                        {viewType === TopicViewType.AskAnExpert && (
                            <div className={styles.Topic__search__container}>
                                <ClearableInput
                                    icon={
                                        <label htmlFor="search">
                                            <FontAwesomeIcon icon={faSearch} />
                                        </label>
                                    }
                                    hasValue={Boolean(this.state.query)}
                                    onClear={this.onCancelFilters}
                                >
                                    <input
                                        id="search"
                                        type="text"
                                        value={this.state.query || ""}
                                        onChange={this.onSearchChange}
                                        placeholder={COUNSELLING_SEARCH_COUNSELLING_PLACEHOLDER}
                                        autoFocus
                                    />
                                </ClearableInput>
                            </div>
                        )}
                        {viewType === TopicViewType.AskAnExpert && query ? (
                            <SearchResults
                                loadingState={loadingState}
                                hasData={Boolean(posts && posts.length > 0)}
                                onCancelFilters={this.onCancelFilters}
                            >
                                <Interpolate
                                    i18nKey={COUNSELLING_SEARCH_COUNSELLING_RESULT_query_count}
                                    query={<strong>{query}</strong>}
                                    count={counsellingPostsCount}
                                />
                            </SearchResults>
                        ) : null}
                    </div>
                ) : (
                    <TopicHeaderPlaceholder />
                )}

                {isDeleted && (
                    <AnnouncementBox
                        message={TOPIC_HAS_BEEN_DELETED}
                        hasDetailMessage={isAdmin || isModerator}
                        detailMessage={topic.deletedReason}
                        deletedAt={topic.deletedAt}
                        deletedBy={isAdmin && topic.deletedBy ? topic.deletedBy.username : null}
                        icon={fasTrash}
                        type={BoxType.Deleted}
                        marginLeft={isMobile ? 5 : undefined}
                        marginRight={isMobile ? 5 : undefined}
                    />
                )}
                {(!isDeleted || (isDeleted && isAdmin)) && this.renderNormalFeed()}
            </div>
        );
    }

    private renderNormalFeed() {
        const {
            topic,
            loadingState,
            counselling,
            isMobile,
            posts,
            after,
            hasNext,
            hasPrevious,
            baseUrl,
            errorMessage,
            complaintsStatistics,
            location,
            query,
        } = this.props;

        const isDeleted = topic && !!topic.deletedAt;

        return (
            <Feed
                posts={posts}
                after={after}
                hasNext={hasNext}
                hasPrevious={hasPrevious}
                isMobile={isMobile}
                errorMessage={errorMessage}
                baseUrl={baseUrl}
                complaintsStatistics={complaintsStatistics}
                location={location}
                showCreated={!topic || topic.id !== TESTINGS_TOPIC_ID}
                showSticky
                noResults={
                    counselling ? (
                        query ? null : (
                            <Interpolate
                                i18nKey={NO_QUESTIONS_FOR_THIS_COUNSELLING_action_btn}
                                action_btn={
                                    <Link to={url(counsellingsNewUrl, { topicSlug: topic.slug })}>
                                        {COUNSELLING_DO_SOMETHING_ABOUT_IT}
                                    </Link>
                                }
                            />
                        )
                    ) : (
                        THERES_NO_POSTS_IN_TOPIC_DO_SOMETHING_WITH_IT
                    )
                }
                loadingState={loadingState}
                backButton={
                    <Btn
                        type={BtnType.Blue}
                        label={BACK_TO_TOPICS}
                        icon={faArrowLeft}
                        link={forumTopicsUrl}
                    />
                }
                onLoad={this.onLoad}
                readonlyPosts={isDeleted}
            />
        );
    }

    private onLoad = (after?: number) => {
        const {
            onLoad,
            hashtags,
            query,
            match: {
                params: { topicSlug },
            },
        } = this.props;
        onLoad(topicSlug, hashtags, query, after);
    };

    private onRead = () => {
        const { topic, onRead, onTopicRead } = this.props;
        // Read only once
        this.setState({ alreadyRead: true });
        onTopicRead(topic.id);
        if (onRead) {
            onRead(topic.id);
        }
    };

    private commonRedirect = (query) => {
        const {
            match: { params },
        } = this.props;

        const search = {
            ...(query !== null ? { q: query } : {}),
        };

        this.props.history.replace({
            pathname: url(counsellingsTopicUrl, params),
            search: search ? stringify(search) : undefined,
        });
    };

    private onSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ query: event.currentTarget.value }, this.searchRedirectDebounced);
    };

    private searchRedirect = () => {
        this.commonRedirect(this.state.query);
    };

    private onCancelFilters = () => {
        this.setState({ query: null });
        this.commonRedirect(null);
    };
}

export default Topic;
